import { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Empty, Select, Spin, message } from 'antd';
import {
    AppstoreOutlined,
    CalendarOutlined,
    CameraOutlined,
    CheckCircleFilled,
    DownloadOutlined,
    EditOutlined,
    PlusCircleFilled,
    RightOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import { toPng } from 'html-to-image';
import SelectCompanyCode from '../../components/Planning/SelectCompany';
import { useFmsDeepDive, useReportConfig, useReportSummary } from '../../hooks/useReports';
import ConfigureReportModal from './ConfigureReportModal';
import DrilldownModal from './DrilldownModal';
import FmsDeepDivePanel from './FmsDeepDivePanel';
import './reports.scss';

function rangeParams(range, fromKey, toKey) {
    if (!range?.[0] || !range?.[1]) return {};
    return {
        [fromKey]: range[0].format('YYYY-MM-DDTHH:mm:ssZ'),
        [toKey]: range[1].format('YYYY-MM-DDTHH:mm:ssZ'),
    };
}

function countOf(counts, bucket) {
    return counts[bucket] || 0;
}

function formatCount(value) {
    return value.toLocaleString();
}

function localTzName() {
    try {
        return (
            Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
                .formatToParts(new Date())
                .find(part => part.type === 'timeZoneName')?.value || ''
        );
    } catch {
        return '';
    }
}

function formatDateTime(value) {
    const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return null;
    return date.toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}

function formatRangeLabel(from, to) {
    const start = formatDateTime(from);
    const end = formatDateTime(to);
    if (!start || !end) return null;
    const tz = localTzName();
    return tz ? `${start} – ${end} ${tz}` : `${start} – ${end}`;
}

const ALL_PROJECTS = 'all';

function selectedProjectIds(ids) {
    return (ids || []).filter(id => id && id !== ALL_PROJECTS);
}

function onProjectIdsChange(values, setProjectTypeIds) {
    if (!values.length || values[values.length - 1] === ALL_PROJECTS) {
        setProjectTypeIds([ALL_PROJECTS]);
        return;
    }
    setProjectTypeIds(values.filter(id => id !== ALL_PROJECTS));
}

function projectFilter(project) {
    return project.id !== ALL_PROJECTS ? { projectTypeId: project.id } : {};
}

function withCount(item, counts, tone) {
    return { ...item, value: countOf(counts, item.bucket), tone };
}

function stageCellModel(counts, spec) {
    const items = spec.filtered.map(item => withCount(item, counts, item.tone));
    const hero = items.find(item => item.tone === 'pending');
    const rest = items.filter(item => item !== hero);
    const total = items.reduce((sum, item) => sum + item.value, 0);
    return { hero, rest, items, totalHint: `${formatCount(total)} total` };
}

function StageStatusCell({ counts, spec, title, onOpen, extra }) {
    const { hero, rest, items, totalHint } = stageCellModel(counts, spec);
    const barTotal = items.reduce((sum, item) => sum + item.value, 0);

    return (
        <div className={`reports-status-cell${hero?.value ? '' : ' is-clear'}`}>
            <div className="reports-status-top">
                <button
                    type="button"
                    className="reports-status-pending"
                    onClick={() => hero && onOpen(title, hero.bucket, hero.label)}
                >
                    {hero?.value ? <strong>{formatCount(hero.value)}</strong> : <CheckCircleFilled />}
                    <span>pending</span>
                </button>
                <div className="reports-status-actions">
                    <button
                        type="button"
                        className="reports-status-view"
                        onClick={() => hero && onOpen(title, hero.bucket, hero.label)}
                    >
                        View SKUs ↗
                    </button>
                    {extra}
                </div>
            </div>
            <div className="reports-status-mix">
                {rest.map(item => (
                    <button
                        key={item.bucket}
                        type="button"
                        className={`reports-status-chip is-${item.tone}`}
                        onClick={() => onOpen(title, item.bucket, item.label)}
                    >
                        <i />
                        <b>{formatCount(item.value)}</b>
                        {item.short || item.label.toLowerCase()}
                    </button>
                ))}
                <span className="reports-status-total">{totalHint}</span>
            </div>
            <div className="reports-assignment-bar" aria-hidden="true">
                {items.map(item => (
                    <span
                        key={item.bucket}
                        className={`is-${item.tone}`}
                        style={{ width: `${barTotal ? (item.value / barTotal) * 100 : 0}%` }}
                    />
                ))}
            </div>
        </div>
    );
}

const DELIVERY_TILE_TONE = {
    done: 'is-done',
    new: 'is-added',
    partial: 'is-partial',
};

function DeliveryInPeriodCell({ counts, spec, title, onOpen }) {
    const items = spec.items.map(item => withCount(item, counts, item.tone));

    return (
        <div className="reports-move is-delivery">
            <div className="reports-move-head">
                <strong>Delivered in period</strong>
            </div>
            <div className="reports-delivery-tiles">
                {items.map(item => (
                    <button
                        key={item.bucket}
                        type="button"
                        className={`reports-move-tile ${DELIVERY_TILE_TONE[item.tone] || 'is-done'}`}
                        onClick={() => onOpen(title, item.bucket, item.label)}
                    >
                        <CheckCircleFilled />
                        <span>
                            <em>{item.label}</em>
                            <strong>{formatCount(item.value)}</strong>
                        </span>
                        <RightOutlined />
                    </button>
                ))}
            </div>
        </div>
    );
}

function ActivityMovementCell({ counts, spec, title, onOpen }) {
    const opening = withCount(spec.opening, counts, 'neutral');
    const added = withCount(spec.added, counts, 'new');
    const completed = withCount(spec.completedInPeriod, counts, 'done');
    const closing = withCount(spec.closing, counts, 'pending');
    const backlog = closing.value - opening.value;
    const backlogTone = backlog > 0 ? 'is-up' : backlog < 0 ? 'is-down' : '';

    return (
        <div className="reports-move">
            <div className="reports-move-head">
                <strong>Pending movement</strong>
                <span className={`reports-move-badge ${backlogTone}`}>
                    {backlog > 0 ? '↑ ' : backlog < 0 ? '↓ ' : ''}
                    {formatCount(Math.abs(backlog))} pending
                </span>
            </div>
            <div className="reports-move-flow">
                <div className="reports-move-col">
                    <span>At start</span>
                    <button
                        type="button"
                        className="reports-move-tile is-start"
                        onClick={() => onOpen(title, opening.bucket, opening.label)}
                    >
                        <strong>{formatCount(opening.value)}</strong>
                        <em>pending</em>
                        <RightOutlined />
                    </button>
                </div>
                <span className="reports-move-arrow" aria-hidden="true">
                    →
                </span>
                <div className="reports-move-col is-mid">
                    <span>During period</span>
                    <button
                        type="button"
                        className="reports-move-tile is-added"
                        onClick={() => onOpen(title, added.bucket, added.label)}
                    >
                        <PlusCircleFilled />
                        <span>
                            <em>Added</em>
                            <strong>{formatCount(added.value)}</strong>
                        </span>
                        <RightOutlined />
                    </button>
                    <button
                        type="button"
                        className="reports-move-tile is-done"
                        onClick={() => onOpen(title, completed.bucket, completed.label)}
                    >
                        <CheckCircleFilled />
                        <span>
                            <em>Completed</em>
                            <strong>{formatCount(completed.value)}</strong>
                        </span>
                        <RightOutlined />
                    </button>
                </div>
                <span className="reports-move-arrow" aria-hidden="true">
                    →
                </span>
                <div className="reports-move-col">
                    <span>At end</span>
                    <button
                        type="button"
                        className="reports-move-tile is-end"
                        onClick={() => onOpen(title, closing.bucket, closing.label)}
                    >
                        <strong>{formatCount(closing.value)}</strong>
                        <em>pending</em>
                        <RightOutlined />
                    </button>
                </div>
            </div>
        </div>
    );
}

const ASSIGNMENT_FLOW = {
    opening: { bucket: 'assignmentOpening', label: 'Pending at start', short: 'at start' },
    added: { bucket: 'assignmentNew', label: 'New pendency', short: 'new' },
    completedInPeriod: { bucket: 'assignmentCompletedInRange', label: 'Completed', short: 'completed' },
    closing: { bucket: 'assignmentClosing', label: 'Pending at end' },
    filtered: [
        { bucket: 'assignmentCompleted', label: 'Completed', tone: 'done' },
        { bucket: 'assignmentPending', label: 'Pending', tone: 'pending' },
    ],
};

const SHOOT_FLOW = {
    opening: { bucket: 'shootOpening', label: 'Pending at start', short: 'at start' },
    added: { bucket: 'shootNew', label: 'New pendency', short: 'new' },
    completedInPeriod: { bucket: 'shootCompletedInRange', label: 'Completed', short: 'completed' },
    closing: { bucket: 'shootClosing', label: 'Pending at end' },
    filtered: [
        { bucket: 'shootCompleted', label: 'Completed', tone: 'done' },
        { bucket: 'shootPartial', label: 'Partial', tone: 'partial' },
        { bucket: 'shootPending', label: 'Pending', tone: 'pending' },
    ],
};

const PACKING_FLOW = {
    opening: { bucket: 'packingOpening', label: 'Pending at start', short: 'at start' },
    added: { bucket: 'packingNew', label: 'New pendency', short: 'new' },
    completedInPeriod: { bucket: 'packingCompletedInRange', label: 'Completed', short: 'completed' },
    closing: { bucket: 'packingClosing', label: 'Pending at end' },
    filtered: [
        { bucket: 'packingCompleted', label: 'Completed', tone: 'done' },
        { bucket: 'packingPending', label: 'Pending', tone: 'pending' },
    ],
};

const OUTBOUND_FLOW = {
    opening: { bucket: 'outboundOpening', label: 'Pending at start', short: 'at start' },
    added: { bucket: 'outboundNew', label: 'New pendency', short: 'new' },
    completedInPeriod: { bucket: 'outboundCompletedInRange', label: 'Completed', short: 'completed' },
    closing: { bucket: 'outboundClosing', label: 'Pending at end' },
    filtered: [
        { bucket: 'outboundCompleted', label: 'Completed', tone: 'done' },
        { bucket: 'outboundPending', label: 'Pending', tone: 'pending' },
    ],
};

const DELIVERY_ITEMS = [
    { bucket: 'delivered', label: 'Fully delivered', short: 'delivered', tone: 'done' },
    { bucket: 'imageOnly', label: 'Image only', short: 'image only', tone: 'new' },
    { bucket: 'contentOnly', label: 'Content only', short: 'content only', tone: 'partial' },
    { bucket: 'deliveryPending', label: 'Pending', tone: 'pending' },
];

const FLOW_PROCESSES = [
    {
        key: 'assignment',
        step: 1,
        title: 'Assignment & DD approval',
        blurb: 'SKU assignment and digital design approval',
        spec: ASSIGNMENT_FLOW,
    },
    {
        key: 'shoot',
        step: 2,
        title: 'Shoot completion',
        blurb: 'Product photography shoot and QC',
        spec: SHOOT_FLOW,
    },
    {
        key: 'packing',
        step: 3,
        title: 'Packing',
        blurb: 'Asset packing and final QC',
        spec: PACKING_FLOW,
    },
    {
        key: 'outbound',
        step: 4,
        title: 'Outbounded',
        blurb: 'Delivery to downstream systems',
        spec: OUTBOUND_FLOW,
    },
];

const ACTIVITY_PROCESSES = [
    ...FLOW_PROCESSES,
    {
        key: 'delivery',
        step: 5,
        title: 'Delivery',
        blurb: 'Delivered in the selected period',
        spec: {
            items: [
                { bucket: 'deliveredInRange', label: 'Fully delivered', tone: 'done' },
                { bucket: 'imageOnlyInRange', label: 'Image only', tone: 'new' },
                { bucket: 'contentOnlyInRange', label: 'Content only', tone: 'partial' },
            ],
        },
    },
];

const SELECTED_PROCESSES = [
    ...FLOW_PROCESSES,
    {
        key: 'delivery',
        step: 5,
        title: 'Delivery',
        blurb: 'Image and content delivery status',
        spec: { filtered: DELIVERY_ITEMS },
    },
];

const EXCEPTION_ITEMS = [
    { bucket: 'markedAsSupport', label: 'Marked as support' },
    { bucket: 'damagedUnshootable', label: 'Damaged & non-shootable' },
    { bucket: 'reordered', label: 'Reorder' },
];

function ProcessMatrix({ projects, variant, onOpen, onInbound, onDeepDive, deepDiveProjectId, deepDiveLoading }) {
    const processes = variant === 'activity' ? ACTIVITY_PROCESSES : SELECTED_PROCESSES;

    return (
        <div className="reports-activity-wrap">
            <div
                className={`reports-activity${variant === 'activity' ? ' is-movement' : ' is-status'}`}
                style={{ '--project-count': projects.length, '--stage-count': processes.length }}
            >
                <div className="reports-activity-stages">
                    <div className="reports-activity-stages-head">Process stage</div>
                    {processes.map(process => (
                        <div key={process.key} className="reports-matrix-stage">
                            <span>{String(process.step).padStart(2, '0')}</span>
                            <div>
                                <strong>{process.title}</strong>
                                <em>{process.blurb}</em>
                            </div>
                        </div>
                    ))}
                </div>
                {projects.map(project => (
                    <section key={project.id} className="reports-activity-group">
                        <header className="reports-activity-group-head">
                            <strong>{project.name}</strong>
                            <button type="button" onClick={() => onInbound(project)}>
                                {formatCount(project.totalCount)} inbounded SKUs
                            </button>
                        </header>
                        {processes.map(process => {
                            const onOpenStage = (title, bucket, label) => onOpen(project, title, bucket, label);
                            if (variant === 'activity' && process.key === 'delivery') {
                                return (
                                    <DeliveryInPeriodCell
                                        key={process.key}
                                        counts={project.counts || {}}
                                        spec={process.spec}
                                        title={process.title}
                                        onOpen={onOpenStage}
                                    />
                                );
                            }
                            if (variant === 'activity') {
                                return (
                                    <ActivityMovementCell
                                        key={process.key}
                                        counts={project.counts || {}}
                                        spec={process.spec}
                                        title={process.title}
                                        onOpen={onOpenStage}
                                    />
                                );
                            }
                            return (
                                <StageStatusCell
                                    key={process.key}
                                    counts={project.counts || {}}
                                    spec={process.spec}
                                    title={process.title}
                                    onOpen={onOpenStage}
                                    extra={
                                        process.key === 'shoot' ? (
                                            <Button
                                                className="reports-no-export"
                                                size="small"
                                                type="link"
                                                onClick={() => onDeepDive(project.id)}
                                                loading={deepDiveProjectId === project.id && deepDiveLoading}
                                            >
                                                Deep Dive
                                            </Button>
                                        ) : null
                                    }
                                />
                            );
                        })}
                    </section>
                ))}
            </div>
        </div>
    );
}

export default function Reports() {
    const [companyCode, setCompanyCode] = useState(null);
    const [projectTypeIds, setProjectTypeIds] = useState([ALL_PROJECTS]);
    const [inboundRange, setInboundRange] = useState(null);
    const [shootCompletionRange, setShootCompletionRange] = useState(null);
    const [applied, setApplied] = useState(null);
    const [drilldown, setDrilldown] = useState(null);
    const [configOpen, setConfigOpen] = useState(false);
    const [deepDiveProjectId, setDeepDiveProjectId] = useState(null);
    const [exportingStages, setExportingStages] = useState(false);
    const [stageView, setStageView] = useState('selected');
    const stagesRef = useRef(null);

    const { data: config } = useReportConfig(companyCode, !!companyCode);
    const projectTypes = config?.projectTypes || [];
    const { data, isFetching } = useReportSummary(applied, !!applied);
    const deepDiveFilters =
        applied && deepDiveProjectId
            ? deepDiveProjectId === ALL_PROJECTS
                ? applied
                : { ...applied, projectTypeId: deepDiveProjectId }
            : null;
    const deepDive = useFmsDeepDive(deepDiveFilters, !!deepDiveFilters);
    const projects = data?.data?.data?.projects || [];
    const deepDiveProject = projects.find(project => project.id === deepDiveProjectId);
    const inboundLabel = formatRangeLabel(applied?.inboundFrom, applied?.inboundTo);
    const shootLabel = formatRangeLabel(applied?.shootCompletionFrom, applied?.shootCompletionTo);

    useEffect(() => {
        if (!config) return;
        const valid = new Set((config.projectTypes || []).map(type => type._id).filter(Boolean));
        setProjectTypeIds(current => {
            const next = current.filter(id => id === ALL_PROJECTS || valid.has(id));
            return next.length ? next : [ALL_PROJECTS];
        });
    }, [config]);

    const applyFilters = () => {
        const hasInbound = !!(inboundRange?.[0] && inboundRange?.[1]);
        const hasShootCompletion = !!(shootCompletionRange?.[0] && shootCompletionRange?.[1]);
        if (!hasInbound && !hasShootCompletion) {
            message.warning('Add an inbound or shoot-completion range');
            return;
        }
        if (hasInbound && !inboundRange[1].isAfter(inboundRange[0])) {
            message.warning('Inbound end must be after start');
            return;
        }
        if (hasShootCompletion && !shootCompletionRange[1].isAfter(shootCompletionRange[0])) {
            message.warning('Shoot completion end must be after start');
            return;
        }

        setDrilldown(null);
        setDeepDiveProjectId(null);
        const selected = selectedProjectIds(projectTypeIds);
        setApplied({
            companyCode,
            ...(selected.length ? { projectTypeIds: selected.join(',') } : {}),
            ...rangeParams(inboundRange, 'inboundFrom', 'inboundTo'),
            ...rangeParams(shootCompletionRange, 'shootCompletionFrom', 'shootCompletionTo'),
        });
    };

    const exportProcessStages = async () => {
        if (!stagesRef.current) return;
        setExportingStages(true);
        const root = stagesRef.current;
        const wrap = root.querySelector('.reports-matrix-wrap, .reports-activity-wrap');
        const previous = wrap
            ? { overflow: wrap.style.overflow, width: wrap.style.width, maxWidth: wrap.style.maxWidth }
            : null;
        try {
            if (wrap) {
                wrap.style.overflow = 'visible';
                wrap.style.maxWidth = 'none';
                wrap.style.width = 'max-content';
            }
            const width = Math.max(root.scrollWidth, wrap?.scrollWidth || 0, wrap?.firstElementChild?.scrollWidth || 0);
            const height = Math.max(root.scrollHeight, root.offsetHeight);
            const dataUrl = await toPng(root, {
                cacheBust: true,
                pixelRatio: 2,
                backgroundColor: '#ffffff',
                width,
                height,
                style: {
                    width: `${width}px`,
                    height: `${height}px`,
                    overflow: 'visible',
                },
                filter: node => !(node.classList && node.classList.contains('reports-no-export')),
                onclone: (_doc, cloned) => {
                    cloned.style.overflow = 'visible';
                    cloned.style.maxWidth = 'none';
                    cloned.style.width = `${width}px`;
                    cloned.querySelectorAll('.reports-matrix-wrap, .reports-activity-wrap').forEach(el => {
                        el.style.overflow = 'visible';
                        el.style.maxWidth = 'none';
                        el.style.width = 'max-content';
                    });
                },
            });
            const link = document.createElement('a');
            link.download = `${companyCode}-process-stages.png`;
            link.href = dataUrl;
            link.click();
        } catch (error) {
            message.error('Could not export process stages screenshot');
        } finally {
            if (wrap && previous) {
                wrap.style.overflow = previous.overflow;
                wrap.style.width = previous.width;
                wrap.style.maxWidth = previous.maxWidth;
            }
            setExportingStages(false);
        }
    };

    if (!companyCode) {
        return <SelectCompanyCode onCompanyCodeSelected={setCompanyCode} title="Select a Client For Reports" />;
    }

    return (
        <div className="reports-page">
            <div className="reports-header">
                <div>
                    <p className="reports-crumb">{companyCode} / Studio operations</p>
                    <h2>Process overview</h2>
                </div>
                <div className="reports-header-actions">
                    <Button icon={<SettingOutlined />} onClick={() => setConfigOpen(true)}>
                        Configure Report
                    </Button>
                    <Button
                        onClick={() => {
                            setCompanyCode(null);
                            setProjectTypeIds([ALL_PROJECTS]);
                            setApplied(null);
                            setInboundRange(null);
                            setShootCompletionRange(null);
                            setDrilldown(null);
                            setDeepDiveProjectId(null);
                            setConfigOpen(false);
                        }}
                    >
                        <EditOutlined />
                    </Button>
                </div>
            </div>

            <section className="reports-overview">
                <div className="reports-overview-top">
                    <form
                        className="reports-filters"
                        onSubmit={event => {
                            event.preventDefault();
                            applyFilters();
                        }}
                    >
                        <div className="reports-filter is-wide">
                            <label>
                                <AppstoreOutlined /> Projects
                            </label>
                            <Select
                                mode="multiple"
                                allowClear
                                maxTagCount="responsive"
                                placeholder="All projects"
                                value={projectTypeIds}
                                options={[
                                    { value: ALL_PROJECTS, label: 'All' },
                                    ...projectTypes
                                        .filter(type => type._id)
                                        .map(type => ({ value: type._id, label: type.name })),
                                ]}
                                onChange={values => onProjectIdsChange(values, setProjectTypeIds)}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className="reports-filter">
                            <label>
                                <CalendarOutlined /> Inbound date
                            </label>
                            <DatePicker.RangePicker
                                showTime={{ format: 'HH:mm' }}
                                format="DD/MM/YYYY HH:mm"
                                value={inboundRange}
                                onChange={setInboundRange}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div className="reports-filter">
                            <label>
                                <CameraOutlined /> Shoot completion
                            </label>
                            <DatePicker.RangePicker
                                showTime={{ format: 'HH:mm' }}
                                format="DD/MM/YYYY HH:mm"
                                value={shootCompletionRange}
                                onChange={setShootCompletionRange}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <Button type="primary" htmlType="submit" loading={isFetching}>
                            Build summary
                        </Button>
                    </form>
                </div>
                {applied ? (
                    <div className="reports-overview-meta">
                        <span>
                            <AppstoreOutlined /> {projects.map(project => project.name).join(', ') || 'Projects'}
                        </span>
                        {inboundLabel ? (
                            <span>
                                <CalendarOutlined /> Inbound {inboundLabel}
                            </span>
                        ) : null}
                        {shootLabel ? (
                            <span>
                                <CameraOutlined /> Shoot completion {shootLabel}
                            </span>
                        ) : null}
                    </div>
                ) : null}
            </section>

            <div className="reports-level" ref={stagesRef}>
                <div className="reports-stages-head">
                    <div>
                        <h3 className="reports-level-title">Process stages</h3>
                        <p className="reports-level-sub">Track SKU progress across studio operations</p>
                        {applied && (inboundLabel || shootLabel) ? (
                            <p className="reports-export-filters">
                                {inboundLabel ? <span>Inbound {inboundLabel}</span> : null}
                                {shootLabel ? <span>Shoot completion {shootLabel}</span> : null}
                            </p>
                        ) : null}
                    </div>
                    {applied ? (
                        <Button
                            className="reports-no-export"
                            icon={<DownloadOutlined />}
                            onClick={exportProcessStages}
                            loading={exportingStages}
                        >
                            Export screenshot
                        </Button>
                    ) : null}
                </div>
                {!applied ? (
                    <Empty description="Apply at least one date range to get process counts." />
                ) : isFetching && !projects.length ? (
                    <Spin />
                ) : (
                    <>
                        <div className="reports-view-tabs reports-no-export">
                            <button
                                type="button"
                                className={stageView === 'selected' ? 'is-active' : ''}
                                onClick={() => setStageView('selected')}
                            >
                                Selected SKUs
                            </button>
                            <button
                                type="button"
                                className={stageView === 'activity' ? 'is-active' : ''}
                                onClick={() => setStageView('activity')}
                            >
                                Overall activity
                            </button>
                        </div>
                        <p className="reports-level-note">
                            {stageView === 'selected'
                                ? 'Current status of filtered SKUs. Stage totals may differ.'
                                : 'Period activity for filtered projects. Delivery counts are in the selected range.'}
                        </p>
                        <ProcessMatrix
                            projects={projects}
                            variant={stageView === 'selected' ? 'selected' : 'activity'}
                            onInbound={project =>
                                setDrilldown({
                                    bucket: 'inbound',
                                    title: `${project.name} · Inbounded`,
                                    ...projectFilter(project),
                                })
                            }
                            onOpen={(project, title, bucket, label) =>
                                setDrilldown({
                                    bucket,
                                    title: `${project.name} · ${title} · ${label}`,
                                    ...projectFilter(project),
                                })
                            }
                            onDeepDive={setDeepDiveProjectId}
                            deepDiveProjectId={deepDiveProjectId}
                            deepDiveLoading={deepDive.isFetching}
                        />

                        <section className="reports-panel">
                            <h4>Removed & reentered</h4>
                            <div className="reports-exception-table">
                                <div
                                    className="reports-exception-row is-head"
                                    style={{ '--project-count': projects.length }}
                                >
                                    <span>Reason</span>
                                    {projects.map(project => (
                                        <span key={project.id}>{project.name}</span>
                                    ))}
                                </div>
                                {EXCEPTION_ITEMS.map(item => (
                                    <div
                                        key={item.bucket}
                                        className="reports-exception-row"
                                        style={{ '--project-count': projects.length }}
                                    >
                                        <span>{item.label}</span>
                                        {projects.map(project => (
                                            <button
                                                key={project.id}
                                                type="button"
                                                onClick={() =>
                                                    setDrilldown({
                                                        bucket: item.bucket,
                                                        title: `${project.name} · Removed & reentered · ${item.label}`,
                                                        ...projectFilter(project),
                                                    })
                                                }
                                            >
                                                {formatCount(countOf(project.counts || {}, item.bucket))}
                                            </button>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </section>
                    </>
                )}
            </div>

            {deepDiveProjectId ? (
                <div className="reports-level">
                    <h3 className="reports-level-title">
                        Level 2 · {deepDiveProject?.name || 'Project'} · FMS shoot completion
                        {deepDive.data?.data?.data?.foundFileCount
                            ? ` · ${deepDive.data.data.data.foundFileCount.toLocaleString()} files`
                            : ''}
                    </h3>
                    <FmsDeepDivePanel
                        loading={deepDive.isFetching}
                        payload={deepDive.data?.data?.data}
                        onStageClick={stage =>
                            setDrilldown({
                                title: stage.title,
                                skuIds: stage.skuIds || [],
                                ...(deepDiveProjectId !== ALL_PROJECTS ? { projectTypeId: deepDiveProjectId } : {}),
                            })
                        }
                    />
                </div>
            ) : null}

            <ConfigureReportModal
                companyCode={companyCode}
                open={configOpen}
                onClose={() => setConfigOpen(false)}
            />
            <DrilldownModal
                open={!!drilldown}
                title={drilldown?.title}
                filters={
                    drilldown?.projectTypeId ? { ...applied, projectTypeId: drilldown.projectTypeId } : applied
                }
                bucket={drilldown?.bucket}
                skuIds={drilldown?.skuIds}
                onClose={() => setDrilldown(null)}
            />
        </div>
    );
}
